<script setup lang="ts">
import axios from 'axios'
import { Plus } from 'lucide-vue-next'

import CampaignEmailItems from './CampaignEmailItems.vue'
import CampaignEmailStatisticsCards from './CampaignEmailStatisticsCards.vue'
import useContentScopeList, { useContentScopeCriteria } from '@/composables/contentScopeList'
import { contentScopeListParams } from '@/utils/contentScope'
import useCampaignListFilters, { useCampaignListQuery } from '@/views/campaign/composables/campaignListFilters'

import type { Campaign, CampaignSortKey, CampaignStatistics } from '@/types/types'

const { t } = useI18n()
const router = useRouter()
const { permissions, stores } = useLocalStorage()
const { selectedStoreFilter } = useStore()
const { emailSettings, getEmailSettings } = useEmailSettings()
const { isDirection } = useAccess()

const senderDomain = computed(() => emailSettings.value?.sender_domain)
const showDomainWarning = computed(() => senderDomain.value !== undefined && !senderDomain.value.is_verified)

const openDomainConfig = () => {
  router.push({
    name: 'DomainConfig',
    ...(senderDomain.value?.id ? { query: { domainId: String(senderDomain.value.id) } } : {})
  })
}

const isLoading = ref(true)
const isFetching = ref(false)
const storeFilter = ref<number[]>(selectedStoreFilter.value.length ? [...selectedStoreFilter.value] : [])
const campaigns = ref<Campaign[]>([])
const statistics = ref<CampaignStatistics>()

const pageTitle = computed(() => ({
  title: 'pages.campaignEmail.title'
}))

const showCreateButton = computed(() => permissions.value['campaign.create'])

const pagination = ref({
  currentPage: 1,
  lastPage: 1,
  total: 0,
  pageSize: 15
})

const sort = ref<CampaignSortKey>('updated_at')

const { campaignListParams, fetchCampaignTotal } = useCampaignListQuery('email', storeFilter)

const contentScope = useContentScopeList('campaign', {
  storageKey: 'campaign_email',
  fetchTotal: fetchCampaignTotal,
  reload: () => {
    pagination.value.currentPage = 1
    return fetchData()
  },
  countsKey: () => storeFilter.value
})

const hasListFilters = computed(() => contentScope.hasActiveFilters.value)

const { origin, createdBy } = useContentScopeCriteria(contentScope)
const { statusCriterion, sortOptions } = useCampaignListFilters(contentScope, { withDraft: true })

const filterCriteria = computed(() => [origin.value, createdBy.value, statusCriterion.value])

const fetchData = async () => {
  try {
    isFetching.value = true

    const urlParams = {
      page: pagination.value.currentPage,
      per_page: 15,
      sort: sort.value,
      ...campaignListParams(contentScopeListParams(contentScope.filters.value))
    }

    const { data } = await axios.get('campaigns', { params: urlParams })
    const campaignsData = data.data

    pagination.value.total = data.meta.total
    pagination.value.lastPage = data.meta.last_page
    pagination.value.pageSize = data.meta.per_page

    if (campaignsData.length > 0) {
      try {
        const ids = campaignsData.map((campaign) => campaign.id)

        const params = {
          ids,
          type: 'email',
          ...(storeFilter.value.length > 0 &&
            storeFilter.value.length < stores.value.length && { storeIds: storeFilter.value })
        }

        const { data: statisticsResponse } = await axios.get('campaigns/statistics', { params })
        const statisticsMap = new Map(statisticsResponse.data.map((stat) => [stat.campaignId, stat]))

        campaignsData.forEach((campaign) => {
          campaign.statistics = statisticsMap.get(campaign.id) ?? null
        })
      } catch {
        campaignsData.forEach((campaign) => {
          campaign.statistics = null
        })
      }
    }

    campaigns.value = campaignsData
  } finally {
    isLoading.value = false
    isFetching.value = false
  }
}

const changePage = async (page: number) => {
  pagination.value.currentPage = page
  await fetchData()
}

const getGeneralStatistics = async () => {
  try {
    const { data } = await axios.get('campaigns/statistics/general', {
      params: {
        storeIds:
          storeFilter.value.length > 0 && storeFilter.value.length < stores.value.length ? storeFilter.value : null,
        type: 'email'
      }
    })

    statistics.value = data.data
  } catch {
    // Statistics may not be available yet
  }
}

watch(storeFilter, async (newValue) => {
  selectedStoreFilter.value = [...newValue]
  await Promise.all([fetchData(), getGeneralStatistics()])
})

watch(sort, async () => {
  pagination.value.currentPage = 1
  await fetchData()
})

onMounted(async () => {
  await Promise.all([fetchData(), getGeneralStatistics(), getEmailSettings()])
})
</script>

<template>
  <div>
    <TheLoadingPage v-if="isLoading" />
    <div v-else>
      <div>
        <TheListHeader :title="t(pageTitle.title)">
          <template #actions>
            <Button v-if="showCreateButton" variant="default-primary" as-child>
              <router-link :to="{ name: 'CampaignEmailCreatePage' }">
                <Plus />
                {{ t('pages.campaignEmail.cta.create') }}
              </router-link>
            </Button>
          </template>
        </TheListHeader>

        <div class="px-6 pb-6 sm:px-12 sm:pb-12">
          <BaseTip v-if="showDomainWarning" severity="warn" class="mb-6">
            <template #text>
              <p class="font-medium">{{ t('pages.campaignEmail.domainNotVerified.title') }}</p>
              <p>{{ t('pages.campaignEmail.domainNotVerified.description') }}</p>
            </template>
            <template v-if="isDirection" #button>
              <Button variant="outline" class="bg-white" @click="openDomainConfig">
                {{ t('pages.campaignEmail.domainNotVerified.cta') }}
              </Button>
            </template>
          </BaseTip>

          <CampaignEmailStatisticsCards v-if="campaigns.length > 0 && statistics" :statistics="statistics" />

          <div class="mb-6 flex items-center gap-2 text-blue-600">
            <BaseIcon name="HeroArrowPathIconSolid" class="h-5 w-5" />
            <span class="text-sm">{{ t('global.stats.upgradeAt') }}</span>
          </div>

          <ListToolbar class="mb-4" :criteria="filterCriteria">
            <template #leading>
              <MultiSelectStores v-model="storeFilter" :has-label="false" />
            </template>
            <template #trailing>
              <ListSortMenu v-model="sort" :options="sortOptions" />
            </template>
          </ListToolbar>

          <CampaignEmailItems
            v-if="campaigns.length"
            :is-loading="isFetching"
            :pagination="pagination"
            :campaigns="campaigns"
            @update:change-page="changePage"
          />

          <ContentScopeEmptyState
            v-else-if="hasListFilters"
            :is-mine-view="contentScope.isMineView.value"
            :subject="contentScope.subject"
            @reset="contentScope.resetFilters()"
          />

          <Card v-else>
            <CardContent class="flex items-center justify-center py-10">
              <div class="text-center">
                <BaseIcon name="IconoirMail" class="mx-auto mb-4 h-12 w-12 text-foreground-disabled" />
                <h3 class="text-lg font-semibold text-foreground">
                  {{ t('pages.campaignEmail.emptyState.title') }}
                </h3>
                <p class="mt-2 text-sm text-foreground-secondary">
                  {{ t('pages.campaignEmail.emptyState.description') }}
                </p>
                <Button v-if="showCreateButton" variant="default-primary" class="mt-6">
                  <router-link :to="{ name: 'CampaignEmailCreatePage' }">
                    {{ t('pages.campaignEmail.cta.create') }}
                  </router-link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  </div>
</template>
